import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Users, Edit2, Key, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import apiClient from '@/services/api';

const UserManagement = ({ storeId }) => {
  const [users, setUsers] = useState([]);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', email: '', phone: '' });
  const [createForm, setCreateForm] = useState({ 
    name: '', 
    email: '', 
    phone: '', 
    password: '',
    role: 'POS',
    store_id: storeId
  });

  // Get current user to check if Super Admin
  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
  const isSuperAdmin = currentUser.role === 'SUPER_ADMIN';

  useEffect(() => {
    loadUsers();
  }, [storeId]);

  useEffect(() => {
    setCreateForm(prev => ({ ...prev, store_id: storeId }));
  }, [storeId]);

  const loadUsers = async () => {
    try {
      const response = await apiClient.get(`/auth/store-users?store_id=${storeId}`);
      setUsers(response.data || []);
    } catch (error) {
      console.error('Error loading users:', error);
      toast.error('Failed to load users');
    }
  };

  const handleEdit = (user) => {
    setSelectedUser(user);
    setEditForm({
      name: user.name,
      email: user.email,
      phone: user.phone || ''
    });
    setShowEditModal(true);
  };

  const handleSaveEdit = async () => {
    try {
      await apiClient.put(`/auth/users/${selectedUser.id}`, editForm);
      toast.success('User updated successfully!');
      setShowEditModal(false);
      loadUsers();
    } catch (error) {
      console.error('Error updating user:', error);
      const errorMessage = error.response?.data?.detail || 'Failed to update user';
      toast.error(errorMessage);
    }
  };

  const handleCreateUser = async () => {
    if (!createForm.name || !createForm.email || !createForm.password) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      await apiClient.post('/auth/register', createForm);
      toast.success('User created successfully!');
      setShowCreateModal(false);
      setCreateForm({ 
        name: '', 
        email: '', 
        phone: '', 
        password: '',
        role: 'POS',
        store_id: storeId
      });
      loadUsers();
    } catch (error) {
      console.error('Error creating user:', error);
      const errorMessage = error.response?.data?.detail || 'Failed to create user';
      toast.error(errorMessage);
    }
  };

  const handleDeleteUser = async (user) => {
    if (!confirm(`Are you sure you want to delete ${user.name}?\n\nThis action cannot be undone.`)) {
      return;
    }

    try {
      await apiClient.delete(`/auth/users/${user.id}`);
      toast.success('User deleted successfully!');
      loadUsers();
    } catch (error) {
      console.error('Error deleting user:', error);
      const errorMessage = error.response?.data?.detail || 'Failed to delete user';
      toast.error(errorMessage);
    }
  };

  const handleResetPassword = async (user) => {
    if (!confirm(`Reset password for ${user.name}?\n\nA new password will be generated.`)) {
      return;
    }

    try {
      const response = await apiClient.post(`/auth/reset-user-password/${user.id}`);
      
      // Show new password in modal
      alert(`Password Reset Successful!\n\nNew Password: ${response.data.new_password}\n\nPlease share this with ${user.name}.`);
      
      toast.success('Password reset successfully!');
    } catch (error) {
      console.error('Error resetting password:', error);
      const errorMessage = error.response?.data?.detail || 'Failed to reset password';
      toast.error(errorMessage);
    }
  };

  const getRoleBadge = (role) => {
    const colors = {
      'SUPER_ADMIN': 'bg-red-100 text-red-800',
      'ADMIN': 'bg-purple-100 text-purple-800',
      'POS': 'bg-blue-100 text-blue-800',
      'KITCHEN': 'bg-orange-100 text-orange-800',
      'RIDER': 'bg-green-100 text-green-800',
      'KIOSK': 'bg-yellow-100 text-yellow-800'
    };
    return colors[role] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-bold">User Management</h2>
          <p className="text-gray-600 mt-1">Manage store users and their access</p>
        </div>
        <Button onClick={() => setShowCreateModal(true)} className="bg-green-600 hover:bg-green-700">
          <Plus className="w-4 h-4 mr-2" />
          Add User
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {users.map((user) => (
          <Card key={user.id} className="hover:shadow-lg transition-shadow">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center justify-between">
                <div className="flex items-center">
                  <Users className="w-5 h-5 mr-2 text-blue-600" />
                  {user.name}
                </div>
                <span className={`px-2 py-1 rounded text-xs font-bold ${getRoleBadge(user.role)}`}>
                  {user.role}
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div>
                  <p className="text-xs text-gray-600">Email</p>
                  <p className="text-sm font-medium break-all">{user.email}</p>
                </div>
                {user.phone && (
                  <div>
                    <p className="text-xs text-gray-600">Phone</p>
                    <p className="text-sm font-medium">{user.phone}</p>
                  </div>
                )}
                <div>
                  <p className="text-xs text-gray-600">Status</p>
                  <span className={`inline-block px-2 py-1 rounded text-xs font-bold ${user.active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                    {user.active ? 'Active' : 'Inactive'}
                  </span>
                </div>
                <div className="flex gap-2 mt-3 pt-3 border-t">
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={() => handleEdit(user)}
                    className="flex-1"
                  >
                    <Edit2 className="w-3 h-3 mr-1" />
                    Edit
                  </Button>
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={() => handleResetPassword(user)}
                    className="flex-1"
                  >
                    <Key className="w-3 h-3 mr-1" />
                    Reset
                  </Button>
                  {isSuperAdmin && (
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={() => handleDeleteUser(user)}
                      className="text-red-600 hover:bg-red-50"
                    >
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
        
        {users.length === 0 && (
          <div className="col-span-full text-center py-12 text-gray-500">
            <Users className="w-12 h-12 mx-auto mb-3 text-gray-400" />
            <p>No users found</p>
          </div>
        )}
      </div>

      {/* Edit User Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Edit User</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Name *</Label>
              <Input
                value={editForm.name}
                onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                placeholder="User name"
              />
            </div>
            <div>
              <Label>Email *</Label>
              <Input
                type="email"
                value={editForm.email}
                onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                placeholder="user@sspicepizza.com"
              />
            </div>
            <div>
              <Label>Phone</Label>
              <Input
                type="tel"
                value={editForm.phone}
                onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
                placeholder="9876543210"
              />
            </div>
            <div className="bg-yellow-50 border border-yellow-200 rounded p-3">
              <p className="text-sm text-yellow-800">
                <strong>Note:</strong> Role cannot be changed. Use "Reset Password" to generate a new password for this user.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditModal(false)}>Cancel</Button>
            <Button onClick={handleSaveEdit} className="bg-blue-600 hover:bg-blue-700">
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create User Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Create New User</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Name *</Label>
              <Input
                value={createForm.name}
                onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })}
                placeholder="Full name"
              />
            </div>
            <div>
              <Label>Email *</Label>
              <Input
                type="email"
                value={createForm.email}
                onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })}
                placeholder="user@sspicepizza.com"
              />
            </div>
            <div>
              <Label>Phone</Label>
              <Input
                type="tel"
                value={createForm.phone}
                onChange={(e) => setCreateForm({ ...createForm, phone: e.target.value })}
                placeholder="9876543210"
              />
            </div>
            <div>
              <Label>Password *</Label>
              <Input
                type="password"
                value={createForm.password}
                onChange={(e) => setCreateForm({ ...createForm, password: e.target.value })}
                placeholder="Enter password"
              />
            </div>
            <div>
              <Label>Role *</Label>
              <Select value={createForm.role} onValueChange={(value) => setCreateForm({ ...createForm, role: value })}>
                <SelectTrigger>
                  <SelectValue placeholder="Select role" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ADMIN">Admin (Store Manager)</SelectItem>
                  <SelectItem value="POS">POS (Cashier/Order Taker)</SelectItem>
                  <SelectItem value="KITCHEN">Kitchen (KDS Operator)</SelectItem>
                  <SelectItem value="RIDER">Rider (Delivery Person)</SelectItem>
                  <SelectItem value="KIOSK">Kiosk (Self-Service)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded p-3">
              <p className="text-sm text-blue-800">
                <strong>Roles:</strong><br/>
                • <strong>Admin:</strong> Full store access<br/>
                • <strong>POS:</strong> Order taking & billing<br/>
                • <strong>Kitchen:</strong> KDS & order prep<br/>
                • <strong>Rider:</strong> Deliveries only<br/>
                • <strong>Kiosk:</strong> Customer self-order
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateModal(false)}>Cancel</Button>
            <Button onClick={handleCreateUser} className="bg-green-600 hover:bg-green-700">
              Create User
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UserManagement;
